import { RiFileCopyLine, RiStickyNoteFill, RiStickyNoteLine } from "@remixicon/react";
import type { DensitySnapshot, WrappedCoin } from "@screener/protocol";
import { Button } from "@screener/uikit/components/button";
import { LockedInline } from "@screener/uikit/components/locked-inline";
import { TableCell, TableRow } from "@screener/uikit/components/table";
import { Tooltip, TooltipContent, TooltipTrigger } from "@screener/uikit/components/tooltip";
import { cn } from "@screener/uikit/lib/utils";
import type { KeyboardEvent as ReactKeyboardEvent, MouseEvent as ReactMouseEvent } from "react";
import { formatUsd, stateLabel, tickerLabel } from "@/lib/labels";
import { usePlanGate } from "@/lib/plan-gate-context";
import { TerminalRowAction, useTerminalRowTrigger } from "@/terminal/terminal-button";
import { tickerForClipboard } from "./copy-ticker";
import { isUnconfirmed } from "./derive";
import { Elapsed } from "./elapsed";
import { type FavoriteIndicator, FavoriteStarButton } from "./favorite-star-button";
import { formatDistance, formatPrice, formatQty, rowTitle } from "./format";
import { type RowPinMatch, rowPinState } from "./pin-match";
import { PinToggleButton } from "./pin-toggle-button";
import { RoundMark } from "./round-mark";
import { MmFlagBadges, SideBadge } from "./side-badge";
import { StaleMark } from "./stale-mark";
import { VenueMark } from "./venue-mark";
import { VolumeBar } from "./volume-bar";
import { WrappedMark } from "./wrapped-mark";

export type { FavoriteIndicator };

/**
 * Заметка по монете строки — сопоставление клиента (§9 1.13), с тем же
 * различением «неизвестно» / «известно», что и у закрепления ниже: список
 * заметок может быть ещё не прочитан или недоступен, и строка не вправе в
 * этом случае утверждать «пусто» (review stage5-notes-and-pin, п. 3).
 */
export type NoteIndicator = "yes" | "no" | "unknown";

/**
 * Минимальный набор полей клавиатурного события, которого достаточно, чтобы
 * решить, какое действие строки вызвать. Вынесено из React-обработчика ради
 * чистой функции — тем же приёмом, что `deriveDetailState` в
 * `use-density-detail.ts`: гонка типов и раскладок клавиатуры ловится прямым
 * вызовом, без построения DOM.
 */
export interface RowKeyLike {
  key: string;
  /**
   * Физическая клавиша, а не то, что она печатает: `p`/`n` на русской
   * раскладке дают другие буквы через `key`, а `code` от раскладки не
   * зависит (review stage5-notes-and-pin, п. 1).
   */
  code: string;
  ctrlKey: boolean;
  metaKey: boolean;
  altKey: boolean;
}

export type RowKeyAction =
  | "open"
  | "toggle-pin"
  | "open-notes"
  | "copy-ticker"
  | "toggle-favorite"
  | "hide-coin"
  | "open-terminal"
  | null;

/**
 * Решает, какое действие строки вызвать по нажатой клавише — открыть панель
 * (Enter/Space), закрепить уровень (`p`), открыть заметки (`n`), скопировать
 * тикер (`c`) или поставить/снять избранное монеты (`f`, design.md change
 * stage6-favorites, решение 6), скрыть монету (`h`, §75 п. 2) или отправить инструмент «в терминал» (`t`, §10 1.45).
 * Модификаторы Ctrl/Meta/Alt отключают
 * буквенные хоткеи: они уже заняты сочетаниями браузера и системы. Всё
 * остальное (в первую очередь стрелки) возвращает `null` — обработчик строки
 * обязан отдать такое событие дальше, обработчику навигации таблицы.
 */
export function resolveRowKeyAction(event: RowKeyLike): RowKeyAction {
  if (event.key === "Enter" || event.key === " ") {
    return "open";
  }
  if (event.ctrlKey || event.metaKey || event.altKey) {
    return null;
  }
  if (event.code === "KeyP") {
    return "toggle-pin";
  }
  if (event.code === "KeyN") {
    return "open-notes";
  }
  if (event.code === "KeyC") {
    return "copy-ticker";
  }
  if (event.code === "KeyF") {
    return "toggle-favorite";
  }
  if (event.code === "KeyH") {
    return "hide-coin";
  }
  if (event.code === "KeyT") {
    return "open-terminal";
  }
  return null;
}

/** Обработчики действий строки, которые вызывает `dispatchRowKeyAction`. */
export interface RowKeyActionHandlers {
  onOpen: () => void;
  onTogglePin: () => void;
  onOpenNotes: () => void;
  onCopyTicker: () => void;
  onToggleFavorite: () => void;
  /** Скрыть монету строки целиком (`h`, §75 п. 2) — без подтверждения. */
  onHideCoin: () => void;
  /** Нет у строки без кнопки терминала — карточка или недоступное действие: тогда `t` уходит дальше без `preventDefault`. */
  onOpenTerminal?: () => void;
}

/**
 * Разбирает клавиатурное событие строки и вызывает нужный обработчик —
 * общая часть `onKeyDown` строки таблицы и строки карточки (review блок C):
 * обе решают одно и то же по одному и тому же событию, разница только в
 * том, что делать, если действие не найдено (строка таблицы и строка
 * карточки шагают дальше по-разному). Возвращает `true`, когда действие
 * обработано (и `preventDefault` уже вызван) — вызывающая сторона в этом
 * случае не отдаёт событие дальше, обработчику навигации.
 */
export function dispatchRowKeyAction(
  event: RowKeyLike & { preventDefault: () => void },
  handlers: RowKeyActionHandlers,
): boolean {
  const action = resolveRowKeyAction(event);
  if (action === null) {
    return false;
  }
  if (action === "open-terminal" && handlers.onOpenTerminal === undefined) {
    return false;
  }
  event.preventDefault();
  if (action === "open") {
    handlers.onOpen();
  } else if (action === "toggle-pin") {
    handlers.onTogglePin();
  } else if (action === "open-notes") {
    handlers.onOpenNotes();
  } else if (action === "copy-ticker") {
    handlers.onCopyTicker();
  } else if (action === "toggle-favorite") {
    handlers.onToggleFavorite();
  } else if (action === "hide-coin") {
    handlers.onHideCoin();
  } else if (action === "open-terminal") {
    handlers.onOpenTerminal?.();
  }
  return true;
}

/**
 * Кнопки строки не входят в роверный `tabindex` (`tabIndex={-1}`): доступ к
 * их действиям с клавиатуры идёт хоткеями строки (`resolveRowKeyAction`), а
 * не вторым проходом табом — иначе сотня плотностей означала бы не одну, а
 * три точки табуляции на строку (review stage5-notes-and-pin, п. 1).
 * `Enter`/`Space` на самой кнопке по-прежнему не должны параллельно открывать
 * панель строки; остальные клавиши, в первую очередь стрелки, обязаны дойти
 * до обработчика строки, а не быть погашенными здесь.
 */
function stopButtonActivation(event: ReactMouseEvent | ReactKeyboardEvent): void {
  if ("key" in event && event.key !== "Enter" && event.key !== " ") {
    return;
  }
  event.stopPropagation();
  if ("key" in event) {
    event.preventDefault();
  }
}

interface DensityRowProps {
  row: DensitySnapshot;
  /** Перечень обёрнутых монет из приветствия — по нему строка ставит пометку у пары. */
  wrapped: readonly WrappedCoin[];
  /** Инструмент перестал отдавать данные: строка есть, но она устарела. */
  stale: boolean;
  /**
   * Эта строка — единственная точка табуляции таблицы (роверный `tabindex`).
   * Иначе таблица на сотню плотностей означала бы сотню нажатий табом, чтобы
   * её миновать, — а по ней смотрят часами и ходят с клавиатуры.
   */
  focusable: boolean;
  onFocus: () => void;
  onKeyDown: (event: ReactKeyboardEvent<HTMLTableRowElement>) => void;
  /** Открыть детальную панель §9 по этой плотности. */
  onOpen: () => void;
  /** По монете строки есть хотя бы одна заметка §6.4 — сопоставление клиента. */
  hasNote: NoteIndicator;
  /**
   * Уровень строки закреплён §6.7 — сопоставление клиента, не признак
   * снимка. `"unknown"` — список закреплений ещё не прочитан или недоступен:
   * строка не вправе в этом случае утверждать ни «закреплено», ни «нет»
   * (review stage5-notes-and-pin, п. 3).
   */
  pinMatch: RowPinMatch | "unknown" | undefined;
  /** Открыть детальную панель на разделе заметок §9. */
  onOpenNotes: () => void;
  /** Закрепить уровень строки или снять уже стоящее закрепление — одно действие. */
  onTogglePin: () => void;
  /** Свой запрос закрепления этой строки ещё не завершился — кнопка недоступна. */
  pinPending: boolean;
  /**
   * Монета строки отмечена избранным целиком §6.6 — сопоставление клиента,
   * не признак снимка (`isWholeCoinFavorite`). `"unknown"` — тот же смысл,
   * что и у `pinMatch`.
   */
  favoriteMatch: FavoriteIndicator;
  /** Поставить или снять избранное монеты строки целиком — одно действие. */
  onToggleFavorite: () => void;
  /** Свой запрос избранного этой монеты ещё не завершился — кнопка недоступна. */
  favoritePending: boolean;
  /** Скрыть монету строки целиком — хоткей `h` (§75 п. 2); кнопки на строке нет. */
  onHideCoin: () => void;
  /** Скопировать тикер строки в буфер обмена (§9 1.44, `tickerForClipboard`). */
  onCopyTicker: () => void;
}

/**
 * Строка таблицы §9. Всё, что в ней стоит, пришло снимком: клиент ничего
 * не пересчитывает. Исключение — заметка и закрепление (§9 1.13, 1.18):
 * их признака в снимке нет, и строка сопоставляет их сама (web-density-table).
 * `Pending` показывается тускло и только при включённом фильтре — сам фильтр
 * решает, дойдёт ли строка сюда.
 */
/**
 * Подсказка значка заметки называет монету: значок стоит на всех её строках
 * сразу, и без этого одна заметка выглядит размноженной по таблице (§9, 1.13).
 */
export function rowNoteHint(base: string, hasNote: NoteIndicator): string {
  const noteScope = "общие для всех бирж и рынков монеты";
  return hasNote === "unknown"
    ? `Заметки по ${base}: неизвестно — список не прочитан; ${noteScope}`
    : hasNote === "yes"
      ? `Есть заметки по ${base} — ${noteScope}`
      : `Заметки по ${base} — ${noteScope}`;
}

export function DensityRow({
  row,
  wrapped,
  stale,
  focusable,
  onFocus,
  onKeyDown,
  onOpen,
  hasNote,
  pinMatch,
  onOpenNotes,
  onTogglePin,
  pinPending,
  favoriteMatch,
  onToggleFavorite,
  favoritePending,
  onHideCoin,
  onCopyTicker,
}: DensityRowProps) {
  // Хоткей `t` делает то же, что кнопка: отправляет в круг или, при пустом
  // наборе групп, ведёт в настройки. `undefined` — кнопки на строке нет.
  const planGate = usePlanGate();
  const terminalTrigger = useTerminalRowTrigger(row);
  const terminalAvailable = terminalTrigger !== undefined;
  const side = row.side;
  // §9: жёлтым помечается плотность с признаками ММ — и сторона в такой строке
  // называется тем же цветом, каким названы её флаги.
  const flagged = row.mm_flags.length > 0;
  const { driftText, diverged } = rowPinState(row, pinMatch);

  const noteLabel =
    hasNote === "unknown"
      ? "Заметки монеты: неизвестно — список не прочитан"
      : hasNote === "yes"
        ? "Заметки монеты: есть записи"
        : "Заметки монеты: пока пусто";
  const noteTitle = rowNoteHint(row.instrument.base, hasNote);

  return (
    <TableRow
      data-row-key={row.density_id}
      data-side={side}
      data-state-name={row.state}
      title={rowTitle(row, wrapped)}
      tabIndex={focusable ? 0 : -1}
      // Строка открывает диалог: читающий с экрана обязан узнать это до
      // взаимодействия, а не по факту открывшейся панели.
      role="button"
      aria-haspopup="dialog"
      aria-keyshortcuts={terminalAvailable ? "P N C F H T" : "P N C F H"}
      onFocus={onFocus}
      // Открытие панели — и мышью, и с клавиатуры: по таблице ходят стрелками,
      // и строка, открываемая только щелчком, из клавиатурной работы выпадает.
      // Действия закрепления и заметок — теми же хоткеями строки (`p`/`n`),
      // а не второй парой точек табуляции; стрелки при этом остаются за
      // `onKeyDown` таблицы.
      onClick={onOpen}
      onKeyDown={(event) => {
        if (
          dispatchRowKeyAction(event, {
            onOpen,
            onTogglePin,
            onOpenNotes,
            onCopyTicker,
            onToggleFavorite,
            onHideCoin,
            onOpenTerminal: terminalTrigger,
          })
        ) {
          return;
        }
        onKeyDown(event);
      }}
      className={cn(
        "h-7 font-[450] tabular-nums",
        // Фокус показывается фоном, а не кольцом: `border-collapse: collapse`
        // не рисует `box-shadow` на `<tr>`, и кольцо просто не появилось бы.
        "focus-visible:bg-accent focus-visible:text-accent-foreground focus-visible:outline-none",
        isUnconfirmed(row.state) && "opacity-55",
        stale && "opacity-70",
        // Подсветка расхождения якоря с закреплённой ценой (web-density-table
        // «Якорь сдвинулся»): выровненная закреплённая строка её не несёт.
        diverged && "bg-pin-wash",
      )}
    >
      <TableCell className="w-1.5 p-0">
        {/* Полоска стороны — усиление, а не единственный носитель: рядом
            стоит слово BID или ASK. */}
        <span
          aria-hidden="true"
          className={cn("block h-7 w-1.5", side === "bid" ? "bg-bid" : "bg-ask")}
        />
      </TableCell>

      <TableCell className="px-2 text-muted-foreground">
        {/* Биржа знаком и рынком словом во всех табличных видах (1.51, §64 п. 1). */}
        <VenueMark instrument={row.instrument} />
      </TableCell>
      <TableCell className="px-2 font-medium text-foreground">
        {tickerLabel(row.instrument, row.exchange_base)}{" "}
        <WrappedMark base={row.instrument.base} wrapped={wrapped} className="ml-1.5" />
      </TableCell>

      <TableCell className="px-2">
        <SideBadge side={side} flagged={flagged} />
      </TableCell>

      <TableCell className="px-2 text-right">
        {/* Пометка круглости §2 стоит при самой цене якоря, не у края строки
            (web-density-table «Круглая цена якоря помечена»), и гаснет вместе
            с якорем при его миграции на некруглую цену — снимок просто
            перестаёт нести `round_rank`. */}
        {row.round_rank !== null ? (
          <RoundMark rank={row.round_rank} className="mr-1 align-middle text-[10px]" />
        ) : null}
        {formatPrice(row.anchor_price)}
        {driftText !== null ? <span className="ml-1 text-[11px] text-pin">{driftText}</span> : null}
      </TableCell>
      <TableCell className="px-2 text-right">{formatDistance(row.distance_pct)}</TableCell>

      <TableCell className="relative px-2 text-right">
        {/* Полоса по постоянной логарифмической шкале §9 (1.9): её длина
            не зависит ни от состава таблицы, ни от фильтров. */}
        <VolumeBar densityUsd={row.density_usd} side={side} className="inset-y-[3px] right-0" />
        <span className="relative mr-1.5 text-[11px] text-muted-foreground">
          {formatQty(row.density_qty)}
        </span>
        <span className="relative font-medium">{formatUsd(row.density_usd)}</span>
      </TableCell>

      <TableCell className="px-2 text-right text-muted-foreground">
        {row.current_levels_count}
      </TableCell>
      <TableCell className="px-2 text-right">
        <Elapsed sinceMs={row.detected_at_ms} />
      </TableCell>
      <TableCell className="px-2">
        {stateLabel(row.state)}
        {/* Устаревание названо словом, а не одной лишь прозрачностью: тот же
            канал §9 отдан `Pending`, и на глаз две вещи неразличимы. */}
        {stale ? <StaleMark className="ml-1.5 text-[11px]" /> : null}
      </TableCell>
      <TableCell className="px-2 text-right">
        {planGate.mmApproachesLocked ? (
          <LockedInline label="Подходы" text={planGate.mmApproachesText} />
        ) : (
          row.approaches_count
        )}
      </TableCell>

      <TableCell className="px-2">
        <span className="flex items-center gap-1">
          {planGate.mmApproachesLocked ? (
            <LockedInline label="Пометки ММ" text={planGate.mmApproachesText} />
          ) : (
            <MmFlagBadges flags={row.mm_flags} />
          )}
        </span>
      </TableCell>

      <TableCell className="w-[132px] px-1 text-center">
        {/* Пять действий строки §9: копировать тикер, открыть в терминале,
            заметка, закрепление и избранное (1.8, 1.13, 1.18, 1.36, §6.6). Кнопки исключены из роверного
            `tabindex` строки (`tabIndex={-1}`): доступ с клавиатуры —
            хоткеями строки `c`/`t`/`n`/`p`/`f`, а не вторым проходом табом
            (review stage5-notes-and-pin, п. 1). Копирование — такая же
            кнопка, как остальные: действие, доступное только хоткеем, мышью
            недоступно вовсе (review stage5-round-price, п. 4). */}
        {/* Сетка из пяти мест по `size-6` (1.51, §64 п. 3): у каждого значка
            своё место, и без терминала остальные не съезжают. По верху строки
            текста, а не по базовой линии: по ней строка ячейки выходила на
            полпикселя выше сетки, и шаг строк расходился с вычисленным
            (`TABLE_ROW_HEIGHT`, design stage9-table-virtual Д2). */}
        <span className="inline-grid grid-cols-5 place-items-center gap-px align-top">
          <Button
            variant="ghost"
            size="icon-xs"
            tabIndex={-1}
            aria-label={`Скопировать тикер ${tickerForClipboard(row)}`}
            className="size-6 text-muted-foreground"
            onClick={(event) => {
              stopButtonActivation(event);
              onCopyTicker();
            }}
            onKeyDown={stopButtonActivation}
          >
            <RiFileCopyLine className="size-3.5" />
          </Button>
          {/* Открыть в терминале (§10 1.45, §89): кнопка без меню, только когда
              есть соответствие рынка (для TigerTrade — и нативное имя); иначе пустое место. */}
          <TerminalRowAction row={row} />
          <Tooltip>
            <TooltipTrigger
              render={
                <Button
                  variant="ghost"
                  size="icon-xs"
                  tabIndex={-1}
                  aria-label={noteLabel}
                  className={cn(
                    "size-6",
                    hasNote === "yes" ? "text-foreground" : "text-muted-foreground",
                  )}
                  onClick={(event) => {
                    stopButtonActivation(event);
                    onOpenNotes();
                  }}
                  onKeyDown={stopButtonActivation}
                />
              }
            >
              {hasNote === "yes" ? (
                <RiStickyNoteFill className="size-3.5" />
              ) : (
                <RiStickyNoteLine className="size-3.5" />
              )}
            </TooltipTrigger>
            <TooltipContent>{noteTitle}</TooltipContent>
          </Tooltip>
          <PinToggleButton
            pinMatch={pinMatch}
            pending={pinPending}
            onToggle={onTogglePin}
            tabIndex={-1}
            stopActivation={stopButtonActivation}
          />
          <FavoriteStarButton
            match={favoriteMatch}
            pending={favoritePending}
            onToggle={onToggleFavorite}
            tabIndex={-1}
            stopActivation={stopButtonActivation}
          />
        </span>
      </TableCell>
    </TableRow>
  );
}
